import * as React from 'react'
import { View, ViewStyle } from 'react-native'
import styled from '@theme/styled-components'

import Loader from '@components/loader/Loader'

import { RecyclerListView, DataProvider, LayoutProvider } from 'recyclerlistview'

interface Props<T> {
  data: T[]
  renderItem: (item: T, idx: number) => React.ReactElement
  height: number
  numColumn: number
  size?: { width: number; height: number }
  didItemChanged?: (a: T, b: T) => boolean
  onEndReached?: () => void
  containerStyle: ViewStyle
}

const InfiniteList: <T>(props: Props<T>) => React.ReactElement = ({
  data,
  height,
  numColumn,
  size = { width: 0, height: 0 },
  renderItem,
  didItemChanged = (a, b) => a !== b,
  onEndReached,
  containerStyle,
}) => {
  const [containerSize, setContainerSize] = React.useState(size)
  const [loading, setLoading] = React.useState(true)

  const groups = React.useMemo(
    () =>
      Array.from(new Array(Math.ceil(data.length / numColumn))).map((_, idx) =>
        data.slice(idx * numColumn, (idx + 1) * numColumn)
      ),
    [data, numColumn]
  )

  React.useEffect(() => {
    setLoading(true)
    const load = setTimeout(() => setLoading(false), 200)
    return () => clearTimeout(load)
  }, [containerSize.height])

  const layoutProvider = React.useMemo(
    () =>
      new LayoutProvider(
        () => 'LAYOUT',
        (type, dim) => {
          dim.width = containerSize.width
          dim.height = height
        }
      ),
    [height, containerSize]
  )

  const Provider = new DataProvider(didItemChanged)
  const dataProvider = React.useMemo(() => Provider.cloneWithRows(groups), [groups])

  const renderRow = (type: string | number, items: (typeof groups)[number], index: number) => {
    if (numColumn === 1) {
      return renderItem(items[0], 0)
    }

    return <Line>{items.map((item, idx) => renderItem(item, idx * numColumn + index))}</Line>
  }

  if (loading) {
    return (
      <Full
        onLayout={(evt) =>
          setContainerSize({ height: evt.nativeEvent.layout.height, width: evt.nativeEvent.layout.width })
        }>
        <Loading>
          <Loader />
        </Loading>
      </Full>
    )
  }

  return (
    <Full
      onLayout={(evt) =>
        setContainerSize({ height: evt.nativeEvent.layout.height, width: evt.nativeEvent.layout.width })
      }>
      {!!containerSize.height && (
        <RecyclerListView
          key={containerSize.height}
          style={{ height: containerSize.height, width: containerSize.width }}
          dataProvider={dataProvider}
          layoutProvider={layoutProvider}
          rowRenderer={renderRow}
          onEndReached={onEndReached}
          onEndReachedThreshold={size.height * 2}
          renderContentContainer={(props, children) => (
            <View {...props} style={[containerStyle, (props as any).style]}>
              {children}
            </View>
          )}
        />
      )}
    </Full>
  )
}

export default InfiniteList

const Full = styled(View)`
  flex: 1;
`

const Line = styled(View)`
  flex-direction: row;
`

const Loading = styled(View)`
  flex: 1;
  align-items: center;
  justify-content: center;
`
